import { wixUpgradeUrl } from '../utils/wixBilling';
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner, faXmark, faArrowLeft, faArrowUpRightFromSquare, faCircleExclamation, faCreditCard,
} from "@fortawesome/free-solid-svg-icons";
import { useSelectedBot } from "../contexts/SelectedBotContext.js";
import PricingPlans, { TIER_ORDER } from "../components/PricingPlans.js";
import { useUnsavedChanges } from "../contexts/UnsavedChangesContext.js";
import { navigateExternal } from "../utils/navigation.js";
import { canWrite } from "../utils/permissions";
import MessageCredits, { MessageCreditsMeter, useMessageCredits } from "../components/MessageCredits.js";
import BillingInvoices from "../components/BillingInvoices.js";
import "./sectionPages.css";
import "./BotBilling.css";

const BASE = process.env.REACT_APP_API_BASE_URL;

// Per-agent billing page (/billing/:botId).
// - Stripe / Free bots: pricing tiers (Smart / Boost / Ultimo). New paid plans
//   start via in-app Stripe Checkout; switching plan / interval and cancelling
//   an existing paid plan go through the Stripe-hosted customer portal.
// - Wix bots: a "managed by Wix" view with current plan + status and a
//   "Manage in Wix" button (no tiers).
function BotBilling() {
  const { t } = useTranslation();
  const { botId } = useParams();
  const navigate = useNavigate();
  const { requestUnsavedAction } = useUnsavedChanges();
  const { bots, loading: botsLoading, refreshBots, selectedBotId, setSelectedBotId } = useSelectedBot();

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState("month");
  const [pending, setPending] = useState(null); // key of the in-flight action
  const [error, setError] = useState(null);
  // Live Wix install state for bots bound to a wix_instance_id: the
  // app may have been UNINSTALLED on Wix since, in which case the bot is NOT
  // actually managed through Wix and should see the normal Stripe tiers.
  // null = unknown/checking, true = installed, false = confirmed uninstalled.
  const [wixInstalled, setWixInstalled] = useState(null);
  // Shopify (feature 29): undefined = checking, {connected:false} / full status after.
  const [shopifyStatus, setShopifyStatus] = useState(undefined);

  const bot = useMemo(
    () => (bots || []).find((b) => String(b.id) === String(botId)) || null,
    [bots, botId]
  );

  // Keep this page in sync with the global agent selector in the header. Two
  // one-way effects, each reacting ONLY to its own trigger (selector vs route)
  // via refs holding the latest values, so they converge without ping-ponging
  // off each other's change:
  //  - change the agent in the header  -> navigate this page to that agent
  //  - deep-link / "Manage billing" from the list -> reflect it in the header
  const botIdRef = useRef(botId);
  const selRef = useRef(selectedBotId);
  botIdRef.current = botId;
  selRef.current = selectedBotId;

  // Skip the first run so a deep-link / "Manage billing" URL wins on entry: on
  // mount the URL agent is adopted into the selector (below), NOT overridden by
  // a stale localStorage selection. After mount, an actual selector change
  // navigates here.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return; }
    if (selectedBotId && String(selectedBotId) !== String(botIdRef.current)) {
      navigate(`/billing/${selectedBotId}`, { replace: true });
    }
  }, [selectedBotId, navigate]);

  useEffect(() => {
    if (botId && String(botId) !== String(selRef.current)) {
      setSelectedBotId(botId);
    }
  }, [botId, setSelectedBotId]);

  // Refresh on mount so the plan reflects any change just made on Stripe/Wix.
  useEffect(() => { refreshBots(); }, [refreshBots]);

  // Message-credit usage + actions (shared by the top meter and the bottom
  // buy/auto-reload panel so there's a single fetch).
  const onCreditsChanged = useCallback(() => refreshBots({ silent: true }), [refreshBots]);
  const credits = useMessageCredits(botId, onCreditsChanged);

  // Plan catalog (only needed for the Stripe/Free tier view).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${BASE}subscription_plans`);
        const data = await res.json();
        if (alive && Array.isArray(data)) setPlans(data);
      } catch (e) {
        if (alive) setError(t("billing_plans_load_error_retry"));
      } finally {
        if (alive) setPlansLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [t]);

  // ── Plan grouping + savings ──
  const monthlyByName = useMemo(() => {
    const m = {};
    plans.forEach((p) => { if (p.billing_interval !== "year") m[p.name] = p; });
    return m;
  }, [plans]);
  const tiers = useMemo(
    () => TIER_ORDER.map((n) => monthlyByName[n]).filter(Boolean),
    [monthlyByName]
  );

  // ── Current state ──
  const sub = bot?.subscription || null;
  const planName = bot?.subscription_plan?.name || "Free";
  const currentInterval = bot?.subscription_plan?.billing_interval || "month";
  const isInternalSub = sub?.stripe_subscription_id === "internal";
  // Either Stripe id marks it (as onPaidStripe below): a list may come without the cached customer id, and a
  // Wix-bound agent with a Stripe plan must never be shown as "Managed through Wix" (Robert, 18.10.2026).
  const hasActiveStripe =
    (!!sub?.stripe_customer_id || (!!sub?.stripe_subscription_id && sub.stripe_subscription_id !== "internal")) &&
    (sub.status === "active" || sub.status === "trialing");
  // A "Wix candidate" is bound to a Wix instance and has no active Stripe sub.
  // Detection keys on wix_instance_id, NOT the stored refresh token: the token
  // can be null even for a genuinely live install (it is minted from the
  // instance id via client_credentials, so /api/wix/status confirms the install
  // without ever needing the stored token). We still confirm the Wix app is
  // STILL installed before committing to the managed view, so while the live
  // check is in flight we show a spinner (wixChecking) rather than guessing.
  // Otherwise an uninstalled bot flashes "Managed through Wix" before flipping
  // to the tiers. Managed only when the check confirms the app is installed.
  const wixProduct = bot?.wix_product;
  const wixCandidate = !!((bot?.wix_instance_id || wixProduct) && !hasActiveStripe);
  const wixChecking = wixCandidate && wixInstalled === null;
  const isWixManaged = wixCandidate && wixInstalled === true;
  // Shopify is the THIRD rail: a bot bound to a Shopify store bills through
  // Shopify's Managed Pricing — but an ACTIVE Stripe subscription always wins
  // (connecting an existing paid agent must never break its Stripe billing), and
  // Wix keeps precedence for Wix-bound bots. Confirmed via /apps/shopify/status.
  const shopifyCandidate = !hasActiveStripe && !wixCandidate && !!bot;
  const shopifyChecking = shopifyCandidate && shopifyStatus === undefined;
  const isShopifyManaged = shopifyCandidate && !!shopifyStatus?.connected;
  // On a paid Stripe plan → changes/cancel go through the hosted portal.
  // Free / internal → a tier choice starts a fresh in-app Checkout.
  // Accept EITHER Stripe id: a valid subscription can have a null cached
  // stripe_customer_id (the backend resolves the customer from the sub id),
  // and openPortal accepts either — keep this in lockstep so a real paying bot
  // never falls through to a second Checkout.
  const onPaidStripe =
    planName !== "Free" &&
    !isInternalSub &&
    (!!sub?.stripe_customer_id || !!sub?.stripe_subscription_id);
  const currentTier = planName !== "Free" ? planName : null;
  const isPastDue = sub?.status === "past_due" || bot?.status_display?.className === "past-due";
  // A paid Stripe sub set to cancel at period end is still active until
  // valid_until. compute_status_display (backend) yields className "ending" +
  // text "Expires <date>" for exactly this case — surface it and offer
  // reactivation instead of a second "Cancel" button.
  const isCancelling = onPaidStripe && bot?.status_display?.className === "ending";
  const endsText = bot?.status_display?.text || "";

  const busy = pending !== null;

  // Default the toggle to the agent's current interval (so a yearly customer
  // sees Yearly selected and their plan as the current one). Once per load.
  const intervalInitRef = useRef(false);
  useEffect(() => {
    if (bot && !intervalInitRef.current) {
      intervalInitRef.current = true;
      if (bot.subscription_plan?.billing_interval === "year") setBillingCycle("year");
    }
  }, [bot]);

  // Verify the Wix app is still installed for Wix-candidate bots. The backend
  // wix/status reports an instance_id only when the app is genuinely installed
  // (it returns status "uninstalled" with a null instance_id otherwise), so an
  // uninstalled bot drops the "Managed through Wix" view and sees the tiers.
  useEffect(() => {
    if (!(bot?.wix_instance_id || bot?.wix_product) || hasActiveStripe) { setWixInstalled(null); return; }
    let alive = true;
    setWixInstalled(null); // show the spinner while we (re)check this bot
    (async () => {
      try {
        const res = await fetch(`${BASE}wix/status?bot_id=${encodeURIComponent(bot.id)}`);
        const data = await res.json();
        if (alive) setWixInstalled(!!data?.instance_id);
      } catch {
        // Transient error: keep the managed view rather than offering a parallel
        // Stripe checkout to a bot that may still be billed through Wix.
        if (alive) setWixInstalled(true);
      }
    })();
    return () => { alive = false; };
  }, [bot?.id, bot?.wix_instance_id, bot?.wix_product, hasActiveStripe]);

  // Confirm the Shopify binding for candidate bots (fast DB check server-side when
  // not bound; the connected case also brings the Managed-Pricing link).
  useEffect(() => {
    if (!bot?.id || hasActiveStripe || bot?.wix_instance_id || bot?.wix_product) { setShopifyStatus(null); return; }
    let alive = true;
    setShopifyStatus(undefined);
    (async () => {
      try {
        const res = await fetch(`${BASE}apps/shopify/status?bot_id=${encodeURIComponent(bot.id)}`);
        const data = await res.json();
        if (alive) setShopifyStatus(res.ok && data ? data : { connected: false });
      } catch {
        if (alive) setShopifyStatus({ connected: false });
      }
    })();
    return () => { alive = false; };
  }, [bot?.id, bot?.wix_instance_id, bot?.wix_product, hasActiveStripe]);

  // Read-only teammates can't start/modify billing — surface the unified
  // permission-denied modal (same pattern as useUpgrade) instead of letting the
  // request 403 with a confusing generic error.
  const denyIfReadOnly = useCallback(() => {
    if (!canWrite()) {
      window.dispatchEvent(
        new CustomEvent("ub:permission_denied", { detail: { message: "" } })
      );
      return true;
    }
    return false;
  }, []);

  // ── Actions ──
  const startCheckout = useCallback(async (planRow, key) => {
    if (!bot || !planRow) return;
    if (denyIfReadOnly()) return;
    setPending(key);
    setError(null);
    if (window.dataLayer) window.dataLayer.push({ event: "checkout_session_started" });
    try {
      const res = await fetch(`${BASE}create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bot_id: bot.id,
          price_id: planRow.stripe_subscription_plan_id,
          subscription_plan_id: planRow.id,
          coupon_id: planRow.coupon_id,
        }),
      });
      const data = await res.json();
      if (data?.checkout_url) navigateExternal(data.checkout_url);
      else { setError(t("billing_checkout_start_error")); setPending(null); }
    } catch (e) {
      console.error("checkout failed:", e);
      setError(t("billing_checkout_start_error"));
      setPending(null);
    }
  }, [bot, denyIfReadOnly, t]);

  const openPortal = useCallback(async (key) => {
    if (denyIfReadOnly()) return;
    if (!sub?.stripe_customer_id && !sub?.stripe_subscription_id) {
      setError(t("billing_no_stripe_sub"));
      return;
    }
    setPending(key);
    setError(null);
    try {
      const res = await fetch(`${BASE}create-customer-portal-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stripe_customer_id: sub.stripe_customer_id || null,
          stripe_subscription_id: sub.stripe_subscription_id || null,
        }),
      });
      const data = await res.json();
      if (data?.url) navigateExternal(data.url);
      else { setError(t("billing_portal_open_error")); setPending(null); }
    } catch (e) {
      console.error("portal failed:", e);
      setError(t("billing_portal_open_error"));
      setPending(null);
    }
  }, [sub, denyIfReadOnly, t]);

  // Shopify-managed: plan changes happen on Shopify's hosted Managed-Pricing page
  // (new tab; Shopify returns the merchant into the embedded app afterwards).
  const manageInShopify = useCallback(() => {
    if (denyIfReadOnly()) return;
    const url = shopifyStatus?.links?.plan_url || shopifyStatus?.links?.shopify_admin_app;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  }, [shopifyStatus, denyIfReadOnly]);

  const manageInWix = useCallback(async () => {
    if (!bot) return;
    if (denyIfReadOnly()) return;
    setPending("wix");
    setError(null);
    try {
      const res = await fetch(`${BASE}wix/status?bot_id=${encodeURIComponent(bot.id)}`);
      const data = await res.json();
      if (data?.instance_id) {
        navigateExternal(wixUpgradeUrl(data));
      } else if (data?.site_id) {
        navigateExternal(`https://manage.wix.com/dashboard/${data.site_id}/manage-website/plans`);
      } else {
        setError(t("billing_wix_reconnect_error"));
        setPending(null);
      }
    } catch (e) {
      console.error("wix status failed:", e);
      setError(t("billing_wix_open_error"));
      setPending(null);
    }
  }, [bot, denyIfReadOnly, t]);

  // ── Loading / not-found ──
  if (botsLoading && !bot) {
    return (
      <div className="bbp-page">
        <div className="loading-container">
          <FontAwesomeIcon icon={faSpinner} spin className="main-loading-spinner" />
        </div>
      </div>
    );
  }
  if (!bot) {
    return (
      <div className="bbp-page">
        <div className="bbp-notfound">
          <p>{t("billing_agent_not_found")}</p>
          <button className="bbp-back" onClick={() => navigate("/billing")}>
            <FontAwesomeIcon icon={faArrowLeft} /> {t("billing_back_to_billing")}
          </button>
        </div>
      </div>
    );
  }

  const statusText = bot.status_display?.text || (planName === "Free" ? t("billing_free_plan") : t("active"));
  const statusClass = bot.status_display?.className || (planName === "Free" ? "free" : "active");

  return (
    <div className="bbp-page">
      <header className="section-head">
        <button
          type="button"
          className="bbp-back"
          onClick={() => requestUnsavedAction(() => navigate("/billing"))}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> All agents
        </button>
        <h1 className="section-head-title">{t("billing_page_title")}</h1>
        <p className="section-head-sub">
          {currentTier
            ? t("billing_currently_on_plan", { plan: planName }) + (currentInterval === "year" ? t("billing_yearly_suffix") : "") + "."
            : t("billing_currently_free")}{" "}
          {wixChecking || shopifyChecking ? "" : isWixManaged ? t("billing_managed_through_wix_sub") : isShopifyManaged ? t("billing_managed_through_shopify_sub") : t("billing_choose_plan_sub")}
        </p>
      </header>

      {error && (
        <div className="bbp-error">
          <span><FontAwesomeIcon icon={faCircleExclamation} /> {error}</span>
          <button onClick={() => setError(null)} aria-label={t("billing_dismiss")}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}

      {isPastDue && !isWixManaged && !isShopifyManaged && (
        <div className="bbp-pastdue">
          <span><FontAwesomeIcon icon={faCircleExclamation} /> {t("billing_past_due_banner")}</span>
          <button onClick={() => openPortal("pastdue")} disabled={busy}>
            {pending === "pastdue" ? <FontAwesomeIcon icon={faSpinner} spin /> : <><FontAwesomeIcon icon={faCreditCard} /> {t("billing_update_payment")}</>}
          </button>
        </div>
      )}

      {/* Usage meter on top, above the plan toggle / Wix panel. */}
      <MessageCreditsMeter credits={credits} />

      {wixChecking || shopifyChecking ? (
        <div className="loading-container">
          <FontAwesomeIcon icon={faSpinner} spin className="main-loading-spinner" />
        </div>
      ) : isShopifyManaged ? (
        /* ── Shopify-managed view (the Wix panel pattern, third rail) ── */
        <div className="bbp-wix bbp-shopify">
          <div className="bbp-wix-head">
            <div className="bbp-wix-headmain">
              <span className="bbp-wix-badge bbp-badge-shopify">Shopify</span>
              <h2 className="bbp-wix-title">{t("billing_managed_through_shopify_title")}</h2>
              <p className="bbp-wix-text">
                {t("billing_shopify_managed_text")}
              </p>
            </div>
            <button className="bbp-wix-btn" onClick={manageInShopify} disabled={busy || !shopifyStatus?.links?.plan_url}>
              {t("billing_manage_in_shopify")} <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            </button>
          </div>
          <div className="bbp-wix-status">
            <div className="bbp-wix-stat">
              <span>{t("current_plan")}</span>
              <strong>{planName}</strong>
            </div>
            <div className="bbp-wix-stat">
              <span>{t("status")}</span>
              <strong className={`bbp-status ${statusClass}`}>
                <span className="bbp-status-dot" />{statusText}
              </strong>
            </div>
          </div>
        </div>
      ) : isWixManaged ? (
        /* ── Wix-managed view ── */
        <div className="bbp-wix">
          <div className="bbp-wix-head">
            <div className="bbp-wix-headmain">
              <span className="bbp-wix-badge">Wix</span>
              <h2 className="bbp-wix-title">{t("billing_managed_through_wix_title")}</h2>
              <p className="bbp-wix-text">
                {t("billing_wix_managed_text")}
              </p>
            </div>
            <button className="bbp-wix-btn" onClick={manageInWix} disabled={busy}>
              {pending === "wix"
                ? <FontAwesomeIcon icon={faSpinner} spin />
                : <>{planName === "Free" ? t("upgrade_plan") : t("billing_manage_in_wix")} <FontAwesomeIcon icon={faArrowUpRightFromSquare} /></>}
            </button>
          </div>
          <div className="bbp-wix-status">
            <div className="bbp-wix-stat">
              <span>{t("current_plan")}</span>
              <strong>{planName}</strong>
            </div>
            <div className="bbp-wix-stat">
              <span>{t("status")}</span>
              <strong className={`bbp-status ${statusClass}`}>
                <span className="bbp-status-dot" />{statusText}
              </strong>
            </div>
          </div>
        </div>
      ) : plansLoading ? (
        <div className="loading-container">
          <FontAwesomeIcon icon={faSpinner} spin className="main-loading-spinner" />
        </div>
      ) : tiers.length === 0 ? (
        <div className="bbp-notfound"><p>{t("billing_no_plans_available")}</p></div>
      ) : (
        /* ── Stripe / Free tier view (rk- design) ── */
        <PricingPlans
          plans={plans}
          billingCycle={billingCycle}
          onBillingCycleChange={setBillingCycle}
          currentTier={currentTier}
          currentInterval={currentInterval}
          onPaidStripe={onPaidStripe}
          isCancelling={isCancelling}
          endsText={endsText}
          pending={pending}
          busy={busy}
          onChoose={(row, cardKey) => (onPaidStripe ? openPortal(cardKey) : startCheckout(row, cardKey))}
          onPortal={openPortal}
        />
      )}

      {/* Buy-packs + automatic top-up panel (self-gates: shows for any paid
          agent, Stripe- or Wix-billed; locked + an upgrade prompt for Free).
          NEVER for a Shopify-managed agent: App Store rule 1.2.1 forbids charging
          a Shopify merchant off-platform, and these packs are Stripe. There is no
          compliant Shopify equivalent to route them through either: Shopify App
          Pricing has no one-time purchase, and once App Pricing is enabled the
          Billing API rejects ALL new charge creation, one-time included
          (Shopify staff, community.shopify.dev/t/.../32762). Also hidden while the
          Shopify check is still in flight, so the packs never flash before
          disappearing (same reason the panel above waits on shopifyChecking).
          The UI gate is not the authority: the buy endpoint must refuse a
          Shopify-bound bot server-side. */}
      {!shopifyChecking && !isShopifyManaged && <MessageCredits credits={credits} />}

      {/* Invoices + receipts. Deliberately NOT rail-gated: the endpoint merges
          subscription invoices with message-credit receipts and returns an empty
          list when there is neither, so the component hides itself. A Wix- or
          Shopify-billed agent that bought credits through Stripe still gets its
          own payment history here — this is read-only history of money already
          taken, never a purchase path, so it raises no Shopify 1.2.1 concern.
          The "all invoices in Stripe" link is passed only for a real paid Stripe
          sub: openPortal dead-ends on 'internal' comps and on platform-billed
          agents that have no Stripe customer at all. */}
      <BillingInvoices
        botId={botId}
        onOpenPortal={onPaidStripe ? () => openPortal("invoices") : null}
      />
    </div>
  );
}

export default BotBilling;
